import type { RefObject } from 'react';
import { useMemo, useRef, useState } from 'react';

import type { ResponseError } from '@rtkwlf/asset-data-service';
import noop from 'lodash/noop';
import { useTranslation } from 'react-i18next';

import { ActionDrawer } from '@packages/components/action-drawer/action-drawer';
import { GridActionToolbar } from '@packages/components/grid-action-toolbar/grid-action-toolbar';
import { Box, Button, Stack } from '@packages/fenrir/components';
import type {
  DataGridProProps,
  GridRowModel,
  GridRowSelectionModel,
} from '@packages/fenrir/components/datagrid';
import {
  DataGridPro,
  filterColumns,
  filterFormPropsDefaults,
  getColumnForNewFilter,
  GridFilterPanel,
  GridLogicOperator,
  GridToolbar,
} from '@packages/fenrir/components/datagrid';
import { GridEmptyOverlay } from '@packages/fenrir/components/datagrid/overlay/grid-empty-overlay/grid-empty-overlay';
import { GridErrorOverlay } from '@packages/fenrir/components/datagrid/overlay/grid-error-overlay/grid-error-overlay';
import { useFeatureFlag } from '@packages/platform/feature-flags/use-feature-flag';

import {
  commonSlotProps,
  paginationConfig,
} from '../../assets/assets.constants';
import { AssetListPageHeader } from '../../assets/device-list/asset-type-layout/asset-list-page-header/asset-list-page-header';
import { AssetsExport } from '../assets-export/assets-export';
import type { AssetsSnackbarRef } from '../assets-snackbar/assets-snackbar';
import { AssetsSnackbar } from '../assets-snackbar/assets-snackbar';

import { AssetTableDetail } from './asset-table-detail';
import { AssetsTableBulkActions } from './assets-table-bulk-actions/assets-table-bulk-actions';
import type { assetTableConfig } from './use-asset-table-config/use-asset-table-config';

const EMPTY_ROWS: GridRowModel[] = [];

const slotPropsAdsFenrirV2 = {
  ...commonSlotProps,
  toolbar: {
    csvOptions: { disableToolbarButton: true },
    printOptions: { disableToolbarButton: true },
  },
  filterPanel: {
    logicOperators: [GridLogicOperator.And],
    filterFormProps: {
      ...filterFormPropsDefaults,
      filterColumns,
    },
    getColumnForNewFilter,
  },
};

export interface AssetsDataGridProps {
  assetTableConfig: assetTableConfig;
  rows: GridRowModel[];
  isLoading: boolean;
  isError: boolean;
  error: ResponseError | null;
  total: number;
  snackbarRef: RefObject<AssetsSnackbarRef>;
  datagridProps?: Partial<DataGridProProps>;
}

const sx: DataGridProProps['sx'] = { '--DataGrid-overlayHeight': '300px' };

const autoHeight = () => 'auto' as const;

export const AssetsTable = ({
  assetTableConfig,
  rows,
  isLoading,
  isError,
  error,
  total,
  snackbarRef,
  datagridProps,
}: AssetsDataGridProps) => {
  const { t } = useTranslation();

  const [fullMockedFF] = useFeatureFlag('ADS_FULL_MOCKED');
  const [isFenrirV2FeatureEnabled] = useFeatureFlag('ADS_FENRIR_V2');
  const [risksWIP] = useFeatureFlag('RISKS_IN_UP_WIP');

  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);

  const boxRef = useRef<HTMLDivElement | null>(null);

  const showNetworkError =
    isError && (!error?.response || error.response.status !== 400);

  const showData = rows && !showNetworkError;

  const slots: DataGridProProps['slots'] = useMemo(
    () => ({
      noRowsOverlay: showNetworkError
        ? GridErrorOverlay
        : () => <GridEmptyOverlay />,
      toolbar: (props) => (
        <Box>
          {risksWIP ? (
            <GridActionToolbar {...props} />
          ) : (
            <Stack
              direction="row"
              sx={{
                width: 1,
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
              }}
            >
              <>
                <GridToolbar {...props} />
                {isFenrirV2FeatureEnabled && <AssetsExport />}
              </>
            </Stack>
          )}
        </Box>
      ),

      //TODO: Update this to common fenrir pattern once available - S2CX-12534
      filterPanel: (props) => (
        <>
          {/* Avoid wrapping with any container to prevent layout shifts.
            The filter grid requires the filter panel and content to be direct children. */}
          <GridFilterPanel data-testid="asset-filter-panel" {...props} />
          <Button
            variant="contained"
            size="small"
            data-testid="apply-filters-button"
            onClick={assetTableConfig.applyFilters}
            sx={{
              position: 'absolute',
              // Bottom: 4px container padding + 4px button padding
              bottom: 8,
              // Right: 'Remove filters button' width + 8px gap
              right: 118,
            }}
          >
            {t('ui:apply')}
          </Button>
        </>
      ),
    }),
    [
      showNetworkError,
      risksWIP,
      isFenrirV2FeatureEnabled,
      assetTableConfig.applyFilters,
      t,
    ],
  );

  const slotProps = useMemo(() => {
    if (risksWIP) {
      return {
        toolbar: {
          ...slotPropsAdsFenrirV2.toolbar,
        },
      };
    }

    return slotPropsAdsFenrirV2;
  }, [risksWIP]);

  return (
    <>
      {isFenrirV2FeatureEnabled && !fullMockedFF && (
        <AssetListPageHeader total={total} />
      )}
      {fullMockedFF && !risksWIP && (
        <AssetsTableBulkActions
          count={rowSelectionModel.length}
          onDeselectAll={() => setRowSelectionModel([])}
          onDelete={noop}
          onUpdate={noop}
          onMerge={noop}
          onSplit={noop}
          onRescan={noop}
        />
      )}

      <AssetsSnackbar
        ref={snackbarRef}
        defaultMessage={t('ads:assetsTable.filterLimitMessage')}
        defaultSeverity="error"
        defaultVariant="standard"
      />

      {
        //TODO: Use Fenrir Wrapper for DataGridPro once available
      }
      <Box ref={boxRef} sx={{ width: 1 }}>
        <DataGridPro
          apiRef={assetTableConfig.apiRef}
          rows={showData ? rows : EMPTY_ROWS}
          columns={assetTableConfig.columns}
          loading={isLoading}
          initialState={assetTableConfig.initialState}
          getRowHeight={autoHeight}
          checkboxSelection={fullMockedFF || risksWIP}
          disableRowSelectionOnClick={fullMockedFF || risksWIP}
          getDetailPanelContent={AssetTableDetail}
          getDetailPanelHeight={autoHeight}
          sortingMode="server"
          sortModel={assetTableConfig.sortModel}
          onSortModelChange={assetTableConfig.onSortModelChange}
          pagination
          paginationMode="server"
          paginationModel={assetTableConfig.paginationModel}
          onPaginationModelChange={assetTableConfig.setPaginationModel}
          pageSizeOptions={paginationConfig.assetsList.options}
          rowSelectionModel={rowSelectionModel}
          rowCount={showData ? total : 0}
          slots={slots}
          slotProps={slotProps}
          sx={sx}
          autoHeight
          columnVisibilityModel={assetTableConfig.columnVisibilityModel}
          onColumnVisibilityModelChange={
            assetTableConfig.setColumnVisibilityModel
          }
          disableMultipleColumnsSorting
          filterMode={isFenrirV2FeatureEnabled ? 'server' : undefined}
          disableColumnFilter={isFenrirV2FeatureEnabled ? false : true}
          onFilterModelChange={
            isFenrirV2FeatureEnabled
              ? assetTableConfig.setFilterModel
              : undefined
          }
          {...datagridProps}
        />
        {risksWIP && <ActionDrawer container={boxRef.current} />}
      </Box>
    </>
  );
};
